import React, { useState, useEffect, useMemo } from 'react';
import { fetchStaffs } from '../../utils/supabaseClient';
import '../../styles/ClassForm.css';

// Field configurations for different types of form fields
const formatColumnName = (columnName) => {
  if (!columnName) return '';
  // Convert snake_case to Title Case with spaces
  return columnName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getFieldConfig = (columnName) => {
  const config = {
    type: 'text',
    label: formatColumnName(columnName)
  };

  // Special handling for specific fields
  switch (columnName) {
    case 'class_name':
      return { ...config, type: 'text', required: true, placeholder: 'Enter class name (e.g., class_1A)' };
    case 'class_teacher':
      return { ...config, type: 'text', placeholder: 'Enter class teacher name' };
    case 'description':
      return { ...config, type: 'textarea', placeholder: 'Enter class description' };
    case 'fee':
      return { ...config, type: 'number', required: true, placeholder: 'Enter fee amount', min: '0', step: '0.01' };
    default:
      return config;
  }
};

const ClassForm = ({ initialData = {}, onSave, onClose, isEditMode = false, columns = [], isSaving = false }) => {
  const [teachers, setTeachers] = useState([]);
  
  // Initialize form data with empty values for all columns
  const [formData, setFormData] = useState(
    columns.reduce((acc, column) => {
      const config = getFieldConfig(column);
      acc[column] = initialData[column] ?? (config.default !== undefined ? config.default : '');
      return acc;
    }, {})
  );

  // Fetch teachers on component mount
  useEffect(() => {
    const loadTeachers = async () => {
      try {
        const staffsData = await fetchStaffs();
        const teachersOnly = (staffsData || []).filter(s => s.role === 'Teacher' && s.is_active !== false);
        setTeachers(teachersOnly);
      } catch (error) {
        console.error('Error loading teachers:', error);
        setTeachers([]);
      }
    };
    loadTeachers();
  }, []);

  // Update form data when initialData changes (for edit mode)
  useEffect(() => {
    if (isEditMode && initialData) {
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));
    }
  }, [initialData, isEditMode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Convert empty strings to null for database
    const processedData = Object.keys(formData).reduce((acc, key) => {
      acc[key] = formData[key] === '' ? null : formData[key];
      return acc;
    }, {});
    
    onSave(processedData);
  };

  // Get required fields from columns that have required: true in their config
  const requiredFields = useMemo(() => 
    columns.filter(col => getFieldConfig(col).required),
    [columns]
  );

  // Check if all required fields are filled
  const isFormValid = useMemo(() => 
    requiredFields.every(field => {
      const value = formData[field];
      return value !== undefined && value !== null && String(value).trim() !== '';
    }),
    [requiredFields, formData]
  );

  // Render form field based on type
  const renderField = (fieldName) => {
    const config = getFieldConfig(fieldName);
    const value = formData[fieldName] ?? '';

    // Special handling for class_teacher field - show dropdown with teacher names
    if (fieldName === 'class_teacher') {
      return (
        <div className="form-group" key={fieldName}>
          <label>
            {config.label || fieldName}
            {config.required && <span className="required"> *</span>}
          </label>
          <select
            name={fieldName}
            value={value}
            onChange={handleChange}
            disabled={isSaving}
          >
            <option value="">Select Teacher</option>
            {teachers.map((teacher) => (
              <option key={teacher.staff_no} value={teacher.staff_no}>
                {teacher.name}
              </option>
            ))}
          </select>
        </div>
      );
    }

    const commonProps = {
      name: fieldName,
      value: value,
      onChange: handleChange,
      placeholder: config.placeholder || `Enter ${config.label || fieldName}`,
      required: config.required,
      disabled: config.disabled || isSaving
    };

    switch (config.type) {
      case 'select':
        return (
          <div className="form-group" key={fieldName}>
            <label>
              {config.label || fieldName}
              {config.required && <span className="required"> *</span>}
            </label>
            <select {...commonProps}>
              {config.options?.map((option, index) => (
                <option key={index} value={option}>
                  {option || `Select ${config.label || fieldName}`}
                </option>
              ))}
            </select>
          </div>
        );

      case 'textarea':
        return (
          <div className="form-group full-width" key={fieldName}>
            <label>
              {config.label || fieldName}
              {config.required && <span className="required"> *</span>}
            </label>
            <textarea
              {...commonProps}
              rows="3"
            />
          </div>
        );

      case 'checkbox':
        return (
          <div className="form-group checkbox" key={fieldName}>
            <label>
              {config.label || fieldName}
            </label>
            <input
              type="checkbox"
              name={fieldName}
              checked={!!value}
              onChange={handleChange}
              disabled={config.disabled || isSaving}
            />
          </div>
        );

      default:
        return (
          <div className="form-group" key={fieldName}>
            <label>
              {config.label || fieldName}
              {config.required && <span className="required"> *</span>}
            </label>
            <input
              type={config.type || 'text'}
              min={config.min}
              step={config.step}
              {...commonProps}
            />
          </div>
        );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="class-form">
      <div className="form-content">
        <div className="form-grid">
          {columns.map(field => renderField(field))}
        </div>
      </div>

      <div className="form-actions">
        <button 
          type="button" 
          className="btn-cancel"
          onClick={onClose}
          disabled={isSaving}
        >
          {isSaving ? 'Please wait...' : 'Cancel'}
        </button>
        <button 
          type="submit" 
          className="btn-submit"
          disabled={!isFormValid || isSaving}
        >
          {isSaving ? (
            <>
              <i className="fas fa-spinner fa-spin"></i>
              <span style={{ marginLeft: '8px' }}>
                {isEditMode ? 'Updating...' : 'Saving...'}
              </span>
            </>
          ) : (
            <>{isEditMode ? 'Update' : 'Add'} Class</>
          )}
        </button>
      </div>
    </form>
  );
};

export default ClassForm;
